import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Grid, GridItem, Image, Flex, Spinner, Text, IconButton, Textarea, ButtonGroup, VStack, Box, Input, useDisclosure } from '@chakra-ui/react'
import { createProjectSectionTaskNote, deleteProjectSectionTaskNote, deleteProjectSectionTaskNoteAttachmentById, deleteProjectSectionTaskNoteAttachmentByPath, getProjectSectionTaskById, getProjectSectionTaskNotesByTaskId, togglePinProjectSectionTaskNote, updateProjectSectionTask, updateProjectSectionTaskNote, uploadProjectSectionTaskNoteAttachment } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { getAssetUrl } from 'lib'
import { format as formatDate } from 'date-fns'
import { MdAttachment, MdDeleteOutline, MdEdit, MdOutlineEdit, MdOutlinePushPin, MdPushPin } from 'react-icons/md'
import { useImmer } from 'use-immer'
import { FILE_ATTACHMENT_STATUS, FileAttachment, ModalConfirmation } from 'components/common'

const NOTE_INITIAL_VALUE = {
  id: 0,
  description: '',
  attachments: []
}

export const DrawerProjectSectionTaskNote = ({ keyForReload, isOpen, onClose, taskId, onEditTask }) => {
  const { currentUser, createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [task, setTask] = useState(null)
  const [notes, setNotes] = useState([])

  const [taskDescription, setTaskDescription] = useState('')
  const [isEditingTaskDescription, setIsEditingTaskDescription] = useState(false)

  const [note, setNote] = useImmer(NOTE_INITIAL_VALUE)
  const [nextAttachmentId, setNextAttachmentId] = useState(1)

  const fileInputRef = useRef(null)

  const [isSaving, setIsSaving] = useState(false)

  const disclosureRemoveNote = useDisclosure()
  const [isRemoving, setIsRemoving] = useState(false)

  useEffect(() => {
    const load = async () => {
      if (!taskId) return
      setIsLoading(true)
      try {
        setTask(await getProjectSectionTaskById(taskId, true))
        setNotes(await getProjectSectionTaskNotesByTaskId(taskId))
      } catch (err) {
        console.log(err)
        createToast('error', 'Error', 'Failed to load notes data.')
      }
      setIsLoading(false)
    }
    load()
    setNote(NOTE_INITIAL_VALUE)
  }, [keyForReload, taskId, isOpen])

  const onEditTaskDescription = () => {
    setTaskDescription(task?.description || '')
    setIsEditingTaskDescription(true)
  }

  const handleSaveTaskDescription = async () => {
    try {
      await updateProjectSectionTask(taskId, { description: taskDescription, sort: task.sort })
      setIsEditingTaskDescription(false)
      setTaskDescription('')
      setTask({ ...task, description: taskDescription })
    } catch (err) {
      createToast('error', 'Error', 'Failed to update task description.')
    }
  }

  const handleCancelEditingTaskDescription = () => {
    setTaskDescription('')
    setIsEditingTaskDescription(false)
  }

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files
      // eslint-disable-next-line space-unary-ops
      for (let i = 0; i < files.length; i ++) {
        const file = files[i]
        const curAttachmentId = nextAttachmentId + i
        const attachment = {
          id: curAttachmentId,
          status: 'uploading',
          progress: 0
        }
        setNote(draft => { draft.attachments = [...draft.attachments, attachment] })
        try {
          const response = await uploadProjectSectionTaskNoteAttachment({ file }, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setNote(draft => {
              draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, progress: percentCompleted } : v)
            })
          })
          setNote(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...response, status: 'success' } : v)
          })
        } catch (err) {
          setNote(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, status: 'fail' } : v)
          })
        }
      }
      setNextAttachmentId(nextAttachmentId + files.length)
    }
  }

  const onAddAttachment = () => {
    fileInputRef.current.click()
  }

  const handleSaveNote = async () => {
    setIsSaving(true)
    try {
      if (note.id) {
        const response = await updateProjectSectionTaskNote(note.id, {
          description: note.description,
          attachments: note.attachments.filter(v => v.status === FILE_ATTACHMENT_STATUS.Success)
        })
        setNotes(notes.map(v => v.id === note.id ? response : v))
      } else {
        const response = await createProjectSectionTaskNote({
          taskId: task.id,
          description: note.description,
          attachments: note.attachments.filter(v => v.status === FILE_ATTACHMENT_STATUS.Success)
        })
        setNotes([...notes, response])
      }
      setNote(NOTE_INITIAL_VALUE)
    } catch (err) {
      createToast('Failed to save note.')
    }
    setIsSaving(false)
  }

  const onEditNote = (note) => {
    setNote(draft => {
      draft.id = note.id
      draft.description = note.description
    })
  }

  const onRemoveNote = (noteId) => {
    setNote(draft => {
      draft.id = noteId
    })
    disclosureRemoveNote.onOpen()
  }

  const handleRemoveNote = async () => {
    setIsRemoving(true)
    try {
      await deleteProjectSectionTaskNote(note.id)
      setNotes(notes.filter(v => v.id !== note.id))
      setNote(NOTE_INITIAL_VALUE)
    } catch (err) {
      createToast('error', 'Error', 'Failed to remove note.')
    }
    setIsRemoving(false)
    disclosureRemoveNote.onClose()
  }

  const handleTogglePin = async (noteId) => {
    try {
      const updated = await togglePinProjectSectionTaskNote(noteId)
      setNotes(prev => {
        const updatedNotes = prev.map(v => v.id === noteId ? { ...v, ...updated } : v)
        return [...updatedNotes].sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))
      })
    } catch (err) {
      createToast('error', 'Error', 'Failed to pin/unpin note.')
    }
  }

  const dueDate = useMemo(() => {
    const date = new Date(task?.dueDate)
    return task?.dueDate ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : 'No due date'
  }, [task?.dueDate])

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement='right'
        onClose={onClose}
        size='lg'
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{isLoading ? 'Loading ...' : task?.title}</DrawerHeader>

          <DrawerBody>
            {!task ? (
              <Text fontSize="sm">No data available.</Text>
            ) : (
              isLoading ? (
                <Flex alignItems="center" gap={4}>
                  <Spinner color="blue.400" size="sm" />
                  <Text fontSize="sm">Loading ...</Text>
                </Flex>
              ) : (
                <>
                  <Grid
                    templateColumns="repeat(5, 1fr)"
                    gap={4}
                    fontSize="xs"
                    alignItems="center"
                    mb={8}
                  >
                    <GridItem colSpan={1} fontWeight="medium">Assignee</GridItem>
                    <GridItem colSpan={4}>
                      <Flex gap={4} alignItems="center">
                        <Image w="28px" h="28px" src={getAssetUrl(task?.assignedUserImage)} borderRadius="full" />
                        <Text>{task?.assignedUserName}</Text>
                        <IconButton
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                          icon={<MdEdit fontSize="18px" />}
                          onClick={() => onEditTask(task)}
                        />
                      </Flex>
                    </GridItem>
                    <GridItem colSpan={1} fontWeight="bold">Due Date</GridItem>
                    <GridItem colSpan={4}>
                      <Flex gap={4} alignItems="center">
                        <Text>{dueDate}</Text>
                        <IconButton
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                          icon={<MdEdit fontSize="18px" />}
                          onClick={() => onEditTask(task)}
                        />
                      </Flex>
                    </GridItem>
                    <GridItem colSpan={1} fontWeight="bold">Created</GridItem>
                    <GridItem colSpan={4}>
                      <Text>{formatDate(new Date(task?.createdAt), 'MM/dd/yyyy hh:mm:ss bbbb')}</Text>
                    </GridItem>
                    <GridItem colSpan={1} fontWeight="bold">Description</GridItem>
                    <GridItem colSpan={4}>
                      {!isEditingTaskDescription && (
                        <IconButton
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                          icon={<MdEdit fontSize="18px" />}
                          onClick={onEditTaskDescription}
                        />
                      )}
                    </GridItem>
                    <GridItem colSpan={5}>
                      {!isEditingTaskDescription ? (
                        <Text>{task?.description || 'Add more detail to this task...'}</Text>
                      ) : (
                        <>
                          <Textarea
                            rows="4"
                            value={taskDescription}
                            onChange={(e) => setTaskDescription(e.target.value)}
                          />
                          <ButtonGroup mt={2}>
                            <Button size="sm" colorScheme="blue" onClick={handleSaveTaskDescription}>Save</Button>
                            <Button size="sm" onClick={handleCancelEditingTaskDescription}>Cancel</Button>
                          </ButtonGroup>
                        </>
                      )}
                    </GridItem>
                  </Grid>
                  {notes.map(note => {
                    return (
                      <VStack
                        key={note.id}
                        gap={2}
                        mt={4}
                        alignItems="flex-start"
                        fontSize="xs"
                        position="relative"
                        bg={note.isPinned ? 'yellow.50' : 'transparent'}
                        borderLeft={note.isPinned ? '3px solid' : 'none'}
                        borderColor={note.isPinned ? 'yellow.400' : 'transparent'}
                        borderRadius={note.isPinned ? 'md' : 'none'}
                        p={note.isPinned ? 3 : 0}
                      >
                        {note.isPinned && (
                          <Flex gap={1} alignItems="center" color="yellow.600" fontSize="2xs" fontWeight="bold">
                            <MdPushPin fontSize="12px" />
                            <Text>Pinned</Text>
                          </Flex>
                        )}
                        <Flex gap={4} alignItems="center">
                          <Image w="28px" h="28px" src={getAssetUrl(note.authorImage)} borderRadius="full" />
                          <Text fontWeight="bold">{note.authorName}</Text>
                          <Text ml={4}>{formatDate(new Date(note.createdAt), 'MM/dd/yyyy hh:mm:ss bbbb')}</Text>
                          <ButtonGroup position="absolute" top={note.isPinned ? 7 : 1} right={2}>
                            <IconButton
                              variant="ghost"
                              size="sm"
                              colorScheme={note.isPinned ? 'yellow' : 'gray'}
                              icon={note.isPinned ? <MdPushPin fontSize="18px" /> : <MdOutlinePushPin fontSize="18px" />}
                              onClick={() => handleTogglePin(note.id)}
                              title={note.isPinned ? 'Unpin' : 'Pin to top'}
                            />
                            {note.authorId === currentUser.id && (
                              <>
                                <IconButton
                                  variant="ghost"
                                  size="sm"
                                  colorScheme="gray"
                                  icon={<MdOutlineEdit fontSize="18px" />}
                                  onClick={() => onEditNote(note)}
                                />
                                <IconButton
                                  variant="ghost"
                                  size="sm"
                                  colorScheme="gray"
                                  icon={<MdDeleteOutline fontSize="18px" />}
                                  onClick={() => onRemoveNote(note.id)}
                                />
                              </>
                            )}
                          </ButtonGroup>
                        </Flex>
                        <Box ml={8}>
                          <div dangerouslySetInnerHTML={{ __html: note.description }} />
                          {note.attachments.length !== 0 && (
                            <Flex gap={4} flexWrap="wrap" my={4}>
                              {note.attachments.map(attachment => {
                                return (
                                  <FileAttachment
                                    key={attachment.path}
                                    canRemove={note.authorId === currentUser.id}
                                    id={attachment.id}
                                    status={FILE_ATTACHMENT_STATUS.Original}
                                    progress={0}
                                    filename={attachment.filename || ''}
                                    extension={attachment.extension || ''}
                                    path={attachment.path || ''}
                                    onRemove={async () => {
                                      try {
                                        await deleteProjectSectionTaskNoteAttachmentById({ id: attachment.id })
                                      } catch (err) {
                                        createToast('error', 'Error', 'Failed to remove attachment.')
                                      }
                                      setNotes(notes.map(v => ({ ...v, attachments: v.attachments.filter(u => u.id !== attachment.id) })))
                                    }}
                                  />
                                )
                              })}
                            </Flex>
                          )}
                        </Box>
                      </VStack>
                    )
                  })}
                  <Box my={8}>
                    <Textarea
                      rows="4"
                      value={note.description}
                      onChange={(e) => setNote(draft => { draft.description = e.target.value })}
                    />
                    {note.attachments.length !== 0 && (
                      <Flex gap={4} flexWrap="wrap" my={4}>
                        {note.attachments.map(attachment => {
                          return (
                            <FileAttachment
                              key={attachment.status === FILE_ATTACHMENT_STATUS.Success ? attachment.path : attachment.id}
                              canRemove={true}
                              id={attachment.id}
                              status={attachment.status}
                              progress={attachment.progress}
                              filename={attachment.filename || ''}
                              extension={attachment.extension || ''}
                              path={attachment.path || ''}
                              onRemove={async () => {
                                if (attachment.status === FILE_ATTACHMENT_STATUS.Success) {
                                  try {
                                    await deleteProjectSectionTaskNoteAttachmentByPath({ path: attachment.path })
                                  } catch (err) {
                                    console.log('Failed to remove temp file on the server.')
                                  }
                                }
                                setNote(draft => {
                                  draft.attachments = draft.attachments.filter(v => {
                                    return attachment.status === FILE_ATTACHMENT_STATUS.Success ? (v.path !== attachment.path) : (v.id !== attachment.id)
                                  })
                                })
                              }}
                            />
                          )
                        })}
                      </Flex>
                    )}
                    <Flex justifyContent="space-between" my={2}>
                      <Flex gap={2} cursor="pointer" alignItems="center" onClick={onAddAttachment}>
                        <IconButton
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                          icon={<MdAttachment fontSize="18px" />}
                        />
                        <Text fontSize="xs">Add Attachment</Text>
                        <Input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} multiple />
                      </Flex>
                      <Button size="sm" colorScheme="blue" onClick={handleSaveNote} isLoading={isSaving}>{!note.id ? 'Add' : 'Update'}</Button>
                    </Flex>
                  </Box>
                </>
              )
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <ModalConfirmation
        heading="Remove Note"
        description="Are you sure to remove the note?"
        btnConfirmLabel="Yes"
        btnCancelLabel="No"
        onConfirm={handleRemoveNote}
        isConfirming={isRemoving}
        {...disclosureRemoveNote}
      />
    </>
  )
}

DrawerProjectSectionTaskNote.propTypes = {
  keyForReload: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  taskId: PropTypes.number,
  onEditTask: PropTypes.func
}
